// src/components/demo/DemoWaiverScreen.js
//
// The rider-held moment (SPEC §4.1 step 5 / §8.3 screen 6): scrollable
// waiver, scroll-to-end gate, agree + age checkboxes, OPTIONAL unchecked
// marketing consent, typed legal name, drawn signature. Guardian variant
// when the rider is a minor.

import React, { useEffect, useRef, useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import { WAIVER_SECTIONS, CONSENT_TEXT } from '../../config/demoWaiver';
import DemoSignaturePad from './DemoSignaturePad';

// 22px squares, ORANGE fill + carbon check when on (design: DWaiver). The
// consent box carries a mono OPTIONAL tag and quieter body weight.
const Check = ({ checked, onChange, children, bold = false, optional = false }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="flex items-start gap-[11px] text-left w-full"
    style={{ minHeight: 44 }}
  >
    <span
      className="flex-shrink-0 flex items-center justify-center"
      style={{
        width: 22, height: 22, marginTop: 2,
        border: '1.5px solid var(--foam-line)',
        background: checked ? 'var(--foam-orange)' : 'var(--foam-paper)',
        color: 'var(--foam-carbon)', fontSize: 14, fontWeight: 700,
      }}
    >
      {checked ? '✓' : ''}
    </span>
    <span
      className={`font-sans text-[13px] ${bold ? 'font-semibold' : ''}`}
      style={{ color: optional ? 'var(--foam-ink-2)' : 'var(--foam-ink)', lineHeight: 1.35 }}
    >
      {children}
      {optional && (
        <span
          className="font-mono block mt-[2px]"
          style={{ fontSize: 9.5, letterSpacing: '0.1em', color: 'var(--foam-mute)' }}
        >
          OPTIONAL
        </span>
      )}
    </span>
  </button>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full font-sans text-[16px] px-[14px]"
    style={{ height: 56, border: '1.5px solid var(--foam-ink)', borderRadius: 0, background: '#FFFFFF' }}
  />
);

export default function DemoWaiverScreen({ rider, onSigned, onBack }) {
  const scrollRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isAdult, setIsAdult] = useState(true);
  const [consent, setConsent] = useState(false); // unchecked by default — SPEC §4.1
  const [typedName, setTypedName] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true);
  };

  // If the waiver text doesn't overflow the box (large screens), there is
  // nothing to scroll — unlock immediately. Re-checked on resize.
  useEffect(() => {
    const check = () => {
      const el = scrollRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 24) setScrolledToEnd(true);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const minorOk = isAdult || (guardianName.trim().length > 1 && guardianRelationship.trim().length > 1);
  const ready = scrolledToEnd && agreed && minorOk && typedName.trim().length > 2 && !!signatureDataUrl;

  return (
    <FScreen
      header={<FHeader right={<FLabel>Liability waiver · v1</FLabel>} />}
      footer={
        <>
          <FBtn
            primary
            disabled={!ready}
            onClick={() => onSigned({
              agreed,
              isMinor: !isAdult,
              guardianName: !isAdult ? guardianName.trim() : null,
              guardianRelationship: !isAdult ? guardianRelationship.trim() : null,
              typedLegalName: typedName,
              signatureDataUrl,
              emailConsent: consent,
              consentText: CONSENT_TEXT,
            })}
          >
            Agree &amp; sign
          </FBtn>
          <FBtn ghost onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <FHeadline size={38}>Sign &amp; ride,<br /><FHeadline.Accent>{rider.firstName}.</FHeadline.Accent></FHeadline>

      {/* Scrollable waiver text — sign controls unlock at the end */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          // flexShrink: 0 is load-bearing: inside FScreen's flex column the box
          // was being crushed to ~2 lines, making the scroll-to-end gate nearly
          // impossible on touch (QA 2026-07-22 — 'can't get past the waiver').
          height: 260, minHeight: 260, flexShrink: 0,
          overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          border: '1.5px solid var(--foam-ink)', background: '#FFFFFF', padding: 16,
        }}
      >
        {WAIVER_SECTIONS.map((s) => (
          <div key={s.heading} style={{ marginBottom: 14 }}>
            <div className="font-sans font-bold text-[13px] uppercase" style={{ letterSpacing: '0.04em' }}>
              {s.heading}
            </div>
            <div className="font-sans text-[13px]" style={{ color: 'var(--foam-ink-2)', whiteSpace: 'pre-line' }}>
              {s.body}
            </div>
          </div>
        ))}
      </div>
      {!scrolledToEnd && <FLabel color="var(--foam-orange)">Scroll to the end to unlock signing</FLabel>}

      <Check checked={agreed} onChange={setAgreed} bold>
        I have read and agree to the Release of Liability, Waiver of Claims, Express Assumption of Risk and
        Indemnity Agreement above.
      </Check>

      <Check checked={isAdult} onChange={setIsAdult}>
        I am 18 years of age or older.
      </Check>

      {!isAdult && (
        <div className="flex flex-col gap-[10px]" style={{ background: 'var(--foam-paper-2)', padding: 14 }}>
          <FLabel>Parent / guardian signs for a minor</FLabel>
          <Input placeholder="Parent/guardian full name" value={guardianName}
            onChange={(e) => setGuardianName(e.target.value)} />
          <Input placeholder="Relationship to rider" value={guardianRelationship}
            onChange={(e) => setGuardianRelationship(e.target.value)} />
        </div>
      )}

      <Check checked={consent} onChange={setConsent} optional>
        {CONSENT_TEXT}
      </Check>

      <div className="flex flex-col gap-[10px]">
        <FLabel>{isAdult ? 'Type your full legal name' : "Guardian: type your full legal name"}</FLabel>
        <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} autoComplete="off" />
      </div>

      <DemoSignaturePad onChange={setSignatureDataUrl} />
    </FScreen>
  );
}
